import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, googleProvider } from './firebaseConfig';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { 
  Container, Typography, Card, CardContent, Grid, Button, TextField, 
  Select, MenuItem, InputLabel, FormControl, RadioGroup, FormControlLabel, 
  Radio, List, ListItem, ListItemText, IconButton, Divider, Dialog, 
  DialogTitle, DialogContent, DialogActions, Box, Paper, ThemeProvider, createTheme, 
  BottomNavigation, BottomNavigationAction, CssBaseline, Switch
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import GoogleIcon from '@mui/icons-material/Google';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WorkIcon from '@mui/icons-material/Work';
import AddCardIcon from '@mui/icons-material/AddCard';
import BarChartIcon from '@mui/icons-material/BarChart';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TuneIcon from '@mui/icons-material/Tune';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const liquidGlassTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#00F0FF' },    
    secondary: { main: '#FFFF00A0' }, 
    background: { default: '#0A0E17', paper: '#161F33' },
    text: { primary: '#E2E8F0', secondary: '#94A3B8' },
    error: { main: '#FF4E4E' },
    success: { main: '#00FF66' }
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 800 }, h5: { fontWeight: 800 }, h6: { fontWeight: 700 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { overflow: 'hidden', overscrollBehavior: 'none' },
        '*::-webkit-scrollbar': { width: '4px', height: '4px' },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': { background: 'rgba(0, 240, 255, 0.3)', borderRadius: '10px' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          backdropFilter: 'blur(4px)',
          borderRadius: 16,
          display: 'flex', flexDirection: 'column'
        }
      }
    },
    MuiButton: { styleOverrides: { root: { borderRadius: 10, fontWeight: 600 } } },
    MuiTextField: { styleOverrides: { root: { '& .MuiOutlinedInput-root': { borderRadius: 10 } } } }
  }
});

const PIE_COLORS = ['#FF4E4E', '#00F0FF', '#FFFF00', '#B366FF', '#00FF66', '#FF9F43'];

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aktuelleSeite, setAktuelleSeite] = useState(0);

  const [kontoStartwert, setKontoStartwert] = useState(0.0);
  const [bargeldStartwert, setBargeldStartwert] = useState(0.0); 
  const [buchungen, setBuchungen] = useState([]);
  const [schichten, setSchichten] = useState([]);
  
  const [kategorien, setKategorien] = useState(['Essen & Trinken', 'Freizeit & Hobbys', 'Miete & Wohnen', 'Gehalt/Job', 'Sonstiges']);
  const [neueKategorie, setNeueKategorie] = useState('');

  const [bTitel, setBTitel] = useState('');
  const [bBetrag, setBBetrag] = useState('');
  const [bIstEinnahme, setBIstEinnahme] = useState('ausgabe');
  const [bKategorie, setBKategorie] = useState('Essen & Trinken');
  const [bZahlungsmethode, setBZahlungsmethode] = useState('Kartengeld'); 
  const [bDatum, setBDatum] = useState(new Date().toISOString().split('T')[0]);

  const [sDatum, setSDatum] = useState(new Date().toISOString().split('T')[0]);
  const [sStunden, setSStunden] = useState('');
  const [sStundensatz, setSStundensatz] = useState('15.00');

  const [selectedBuchung, setSelectedBuchung] = useState(null);
  const [editIndex, setEditIndex] = useState(-1);
  const [editDatum, setEditDatum] = useState('');

  // FILTER & VIEW STATES
  const [statistikenFilter, setStatistikenFilter] = useState('Alle'); 
  const [zeitraumFilter, setZeitraumFilter] = useState('Alle Zeiten'); // NEU
  
  // VIEW TOGGLES (NEU)
  const [zeigeBarChart, setZeigeBarChart] = useState(true);
  const [zeigePieChart, setZeigePieChart] = useState(true);
  const [zeigeViewOptions, setZeigeViewOptions] = useState(false); // Akkordeon für Settings

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!user) return;
    const userDocRef = doc(db, "users", user.uid);
    const unsubscribeDoc = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setKontoStartwert(Number(data.kontoStartwert) || 0.0);
        setBargeldStartwert(Number(data.bargeldStartwert) || Number(data.bargeld) || 0.0);
        setBuchungen(data.buchungenListe || []);
        setSchichten(data.offeneSchichten || []);
        if (data.kategorienListe && data.kategorienListe.length > 0) {
          setKategorien(data.kategorienListe);
        }
      }
    });
    return () => unsubscribeDoc();
  }, [user]);

  const speichereInFirestore = async (neueBuchungen, neueSchichten, neuerStartwert = kontoStartwert, neuerBargeldStartwert = bargeldStartwert, neueKategorien = kategorien) => {
    if (!user) return;
    const userDocRef = doc(db, "users", user.uid);
    await setDoc(userDocRef, {
      themeMode: "Liquid Glass", zeigeStundenrechner: true,
      kontoStartwert: neuerStartwert, 
      bargeldStartwert: neuerBargeldStartwert, 
      buchungenListe: neueBuchungen, 
      offeneSchichten: neueSchichten,
      kategorienListe: neueKategorien
    }, { merge: true });
  };

  const loginMitGoogle = async () => { try { await signInWithPopup(auth, googleProvider); } catch (e) {} };
  const logout = async () => { await signOut(auth); };

  const handleAddKategorie = async () => {
    const formatierteKat = neueKategorie.trim();
    if (!formatierteKat || kategorien.includes(formatierteKat)) return;
    const upToKategorien = [...kategorien, formatierteKat];
    setKategorien(upToKategorien);
    setNeueKategorie('');
    await speichereInFirestore(buchungen, schichten, kontoStartwert, bargeldStartwert, upToKategorien);
  };

  const pruefeIstBarzahlung = (b) => {
    return b.istBar === true || 
           b.istBar === 'true' || 
           b.isBar === true || 
           b.isBar === 'true' || 
           String(b.zahlungsmethode).toLowerCase().includes('bar') || 
           String(b.typ).toLowerCase().includes('bar') || 
           String(b.art).toLowerCase().includes('bar') ||
           String(b.quelle).toLowerCase().includes('bar'); // Für Android Kompatibilität
  };

  // 1. Globale Kontostände berechnen (Immer ALL TIME, unabhängig vom Monats-Filter, sonst wäre Kontostand falsch)
  const { aktuellerKontostand, aktuellerBargeldstand } = useMemo(() => {
    let einnahmenKonto = 0, ausgabenKonto = 0;
    let einnahmenBargeld = 0, ausgabenBargeld = 0;

    buchungen.forEach(b => {
      const betrag = Number(b.betrag);
      const istBar = pruefeIstBarzahlung(b);
      
      if (b.istEinkommen) {
        if (istBar) einnahmenBargeld += betrag; else einnahmenKonto += betrag;
      } else {
        if (istBar) ausgabenBargeld += betrag; else ausgabenKonto += betrag;
      }
    });

    return { 
      aktuellerKontostand: kontoStartwert + einnahmenKonto - ausgabenKonto,
      aktuellerBargeldstand: bargeldStartwert + einnahmenBargeld - ausgabenBargeld
    };
  }, [buchungen, kontoStartwert, bargeldStartwert]);

  // ERMITTLE VERFÜGBARE MONATE FÜR DAS FILTER-DROPDOWN
  const verfuegbareMonate = useMemo(() => {
    const monateSet = new Set();
    buchungen.forEach(b => {
      if (b.datumString) {
        monateSet.add(b.datumString.substring(0, 7)); // 'YYYY-MM'
      }
    });
    return Array.from(monateSet).sort().reverse(); // Neueste zuerst
  }, [buchungen]);

  // 2. Statistiken, Charts & Filter anwenden
  const { monatsDaten, ausgabenNachKategorie, gesamtAusgaben, gefilterteBuchungen, tagesBudgetRest, sparQuote, topAusgabenfalle } = useMemo(() => {
    let ausgabenGesamt = 0;
    let einnahmenGesamt = 0;
    const gruppen = {};
    const kategorieGruppen = {};
    const gefiltert = [];

    buchungen.forEach(b => {
      const betrag = Number(b.betrag);
      const istBar = pruefeIstBarzahlung(b);
      const monatKey = b.datumString ? b.datumString.substring(0, 7) : 'Unbekannt';
      
      // 1. Zahlungsmethoden-Filter
      if (statistikenFilter === 'Bargeld' && !istBar) return;
      if (statistikenFilter === 'Kartengeld' && istBar) return;

      // 2. Zeitraum-Filter (NEU)
      if (zeitraumFilter !== 'Alle Zeiten' && monatKey !== zeitraumFilter) return;

      gefiltert.push(b);

      // Für das BarChart gruppieren wir immer (auch wenn auf einen Monat gefiltert ist, dann gibt's halt nur einen Balken)
      if (!gruppen[monatKey]) gruppen[monatKey] = { label: monatKey.split('-').reverse().join('/'), einnahmen: 0, ausgaben: 0 };
      
      if (b.istEinkommen) {
        gruppen[monatKey].einnahmen += betrag;
        einnahmenGesamt += betrag;
      } else {
        gruppen[monatKey].ausgaben += betrag;
        ausgabenGesamt += betrag;
        kategorieGruppen[b.kategorie] = (kategorieGruppen[b.kategorie] || 0) + betrag;
      }
    });

    const pieData = Object.keys(kategorieGruppen)
      .map(key => ({ name: key, value: kategorieGruppen[key] }))
      .sort((a, b) => b.value - a.value);

    // Tagesbudget (Bezieht sich sinnvollerweise immer auf den aktuellen Monat, unabhängig vom Filter, oder wir nehmen den gefilterten Rest-Stand)
    const heute = new Date();
    const letzterTagDesMonats = new Date(heute.getFullYear(), heute.getMonth() + 1, 0).getDate();
    const verbleibendeTage = letzterTagDesMonats - heute.getDate() + 1;
    const kontostandFuerRechnung = statistikenFilter === 'Bargeld' ? aktuellerBargeldstand : statistikenFilter === 'Kartengeld' ? aktuellerKontostand : (aktuellerKontostand + aktuellerBargeldstand);
    const errechnetesTagesbudget = kontostandFuerRechnung > 0 ? (kontostandFuerRechnung / verbleibendeTage) : 0;

    const errechneteSparquote = einnahmenGesamt > 0 ? Math.max(0, Math.round(((einnahmenGesamt - ausgabenGesamt) / einnahmenGesamt) * 100)) : 0;
    const falle = pieData.length > 0 ? pieData[0].name : 'Keine';

    return { 
      gesamtAusgaben: ausgabenGesamt,
      ausgabenNachKategorie: pieData,
      monatsDaten: Object.keys(gruppen).sort().map(k => ({ name: gruppen[k].label, Einnahmen: gruppen[k].einnahmen, Ausgaben: gruppen[k].ausgaben })),
      gefilterteBuchungen: gefiltert,
      tagesBudgetRest: errechnetesTagesbudget,
      sparQuote: errechneteSparquote,
      topAusgabenfalle: falle
    };
  }, [buchungen, statistikenFilter, zeitraumFilter, aktuellerKontostand, aktuellerBargeldstand]);

  const { aktiveSchichten, offenerLohn, gesamtArbeitsstunden, letzteSchichtenCount, archivGruppiert } = useMemo(() => {
    const aktive = schichten.filter(s => !s.istAbgeschlossen);
    const archivierte = schichten.filter(s => s.istAbgeschlossen);
    const lohn = aktive.reduce((sum, s) => sum + (Number(s.dauerInStunden) * Number(s.stundenlohn)), 0);
    const stunden = aktive.reduce((sum, s) => sum + Number(s.dauerInStunden), 0);

    const gruppiert = {};
    archivierte.forEach(s => {
      const monatKey = s.datumString ? s.datumString.substring(0, 7) : 'Unbekannt';
      if (!gruppiert[monatKey]) gruppiert[monatKey] = [];
      gruppiert[monatKey].push(s);
    });

    const sortierteGruppen = Object.keys(gruppiert).sort().reverse().map(key => ({
      monatKey: key, schichten: gruppiert[key],
      verdienst: gruppiert[key].reduce((sum, s) => sum + (Number(s.dauerInStunden) * Number(s.stundenlohn)), 0),
      istAusgezahlt: gruppiert[key].every(s => s.istAusgezahlt)
    }));

    return { aktiveSchichten: aktive, offenerLohn: lohn, gesamtArbeitsstunden: stunden, letzteSchichtenCount: aktive.length, archivGruppiert: sortierteGruppen };
  }, [schichten]);

  const handleAddBuchung = async (e) => {
    e.preventDefault();
    if (!bTitel || !bBetrag) return;
    await speichereInFirestore([...buchungen, { 
      titel: bTitel, betrag: parseFloat(bBetrag), istEinkommen: bIstEinnahme === 'einnahme', kategorie: bKategorie, 
      istDauerauftrag: false, intervall: "Monatlich", ausfuehrungsTag: 1, datumString: bDatum,
      istBar: bZahlungsmethode === 'Bargeld', 
      zahlungsmethode: bZahlungsmethode,
      quelle: bZahlungsmethode === 'Bargeld' ? 'Bar' : 'Karte' // Redundant absichern für Android
    }], schichten);
    setBTitel(''); setBBetrag(''); setAktuelleSeite(0);
  };

  const handleAddSchicht = async (e) => {
    e.preventDefault();
    if (!sStunden || !sStundensatz) return;
    await speichereInFirestore(buchungen, [...schichten, { datumString: sDatum, dauerInStunden: parseFloat(sStunden), stundenlohn: parseFloat(sStundensatz), istAbgeschlossen: false, istAusgezahlt: false }]);
    setSStunden('');
  };

  const handleMonatAbschliessen = async () => {
    if (aktiveSchichten.length === 0) return;
    const aktualisierteSchichten = schichten.map(s => !s.istAbgeschlossen ? { ...s, istAbgeschlossen: true } : s);
    await speichereInFirestore(buchungen, aktualisierteSchichten);
  };

  const handleArchivAuszahlen = async (monatKey, monatsVerdienst) => {
    const datum = new Date(`${monatKey}-01`);
    const formatierterMonat = datum.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    const aktualisierteSchichten = schichten.map(s => (s.istAbgeschlossen && s.datumString.startsWith(monatKey)) ? { ...s, istAusgezahlt: true } : s);

    await speichereInFirestore([...buchungen, { 
      titel: `Stundenlohn Abrechnung ${formatierterMonat}`, betrag: monatsVerdienst, istEinkommen: true, kategorie: "Gehalt/Job", 
      istDauerauftrag: false, intervall: "Monatlich", ausfuehrungsTag: 1, datumString: new Date().toISOString().split('T')[0], 
      istBar: false, zahlungsmethode: "Kartengeld", quelle: "Karte"
    }], aktualisierteSchichten);
  };

  const handleDeleteBuchung = async (buchungObjekt, e) => {
    e.stopPropagation();
    const indexInOriginal = buchungen.indexOf(buchungObjekt);
    if (indexInOriginal !== -1) {
      await speichereInFirestore(buchungen.filter((_, i) => i !== indexInOriginal), schichten);
    }
  };

  const handleDeleteSchicht = async (schichtObjekt, e) => {
    e.stopPropagation();
    const indexInOriginal = schichten.indexOf(schichtObjekt);
    if (indexInOriginal !== -1) {
      await speichereInFirestore(buchungen, schichten.filter((_, i) => i !== indexInOriginal));
    }
  };

  const openDetailDialog = (buchungObjekt) => {
    setSelectedBuchung(buchungObjekt);
    setEditIndex(buchungen.indexOf(buchungObjekt));
    setEditDatum(buchungObjekt.datumString || new Date().toISOString().split('T')[0]);
  };

  const handleSaveDetail = async () => {
    if (editIndex === -1) return;
    const upToList = [...buchungen];
    upToList[editIndex] = { ...upToList[editIndex], datumString: editDatum };
    
    // NACH ÄNDERUNG DES DATUMS DIE LISTE NEU SORTIEREN
    upToList.sort((a, b) => new Date(a.datumString) - new Date(b.datumString));
    
    await speichereInFirestore(upToList, schichten);
    setSelectedBuchung(null); setEditIndex(-1);
  };

  // Hilfsfunktion zur formatierten Darstellung des Monats
  const formatMonat = (YYYYMM) => {
    if (YYYYMM === 'Alle Zeiten') return 'Alle Zeiten';
    const [year, month] = YYYYMM.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  };

  if (loading) return (
    <ThemeProvider theme={liquidGlassTheme}><CssBaseline />
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'background.default' }}>
        <Typography variant="h6" color="primary.main">Laden...</Typography>
      </Box>
    </ThemeProvider>
  );

  return (
    <ThemeProvider theme={liquidGlassTheme}>
      <CssBaseline />
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', color: 'text.primary' }}>
        
        {!user ? (
          <Container maxWidth="xs" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Paper sx={{ p: 4, width: '100%', textAlign: 'center', border: '1px solid rgba(0, 240, 255, 0.2)' }}>
              <Typography variant="h4" sx={{ color: 'primary.main', mb: 1, fontWeight: 900 }}>Finanzplaner</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>Web-Dashboard & Live-Synchronisation</Typography>
              <Button fullWidth variant="contained" size="large" startIcon={<GoogleIcon />} onClick={loginMitGoogle} sx={{ bgcolor: 'primary.main', color: '#000', py: 1.5 }}>
                Mit Google anmelden
              </Button>
            </Paper>
          </Container>
        ) : (
          <>
            <Box sx={{ px: 2, pt: 3, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ color: 'primary.main', textShadow: '0 0 4px rgba(0,240,255,0.15)' }}>Finanzplaner</Typography>
              <IconButton onClick={logout} color="error" size="small" sx={{ border: '1px solid rgba(255,78,78,0.2)' }}><LogoutIcon fontSize="small" /></IconButton>
            </Box>

            <Container maxWidth="md" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pb: 2, overflow: 'hidden' }}>
              
              {aktuelleSeite === 0 && (
                <Grid container spacing={2} sx={{ height: '100%', alignContent: 'flex-start' }}>
                  <Grid item xs={6}>
                    <Card sx={{ p: 1.5, background: 'linear-gradient(145deg, #161F33 0%, #0d1524 100%)' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AccountBalanceWalletIcon fontSize="small"/> Kontostand</Typography>
                      <Typography variant="h5" sx={{ mt: 1, color: aktuellerKontostand >= 0 ? 'success.main' : 'error.main' }}>
                        {aktuellerKontostand.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card sx={{ p: 1.5, background: 'linear-gradient(145deg, #161F33 0%, #0d1524 100%)' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AccountBalanceWalletIcon fontSize="small"/> Bargeld</Typography>
                      <Typography variant="h5" sx={{ mt: 1, color: aktuellerBargeldstand >= 0 ? 'success.main' : 'error.main' }}>
                        {aktuellerBargeldstand.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <Card sx={{ p: 1.5, background: 'linear-gradient(145deg, #161F33 0%, #0d1524 100%)' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><WorkIcon fontSize="small"/> Offener Lohn</Typography>
                      <Typography variant="h5" sx={{ mt: 1, color: 'secondary.main' }}>
                        {offenerLohn.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                      </Typography>
                    </Card>
                  </Grid>
                  
                  {monatsDaten.length > 0 && (
                    <Grid item xs={12}>
                      <Card sx={{ p: 1, height: '140px' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Monatsbilanz</Typography>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={monatsDaten} margin={{ top: 0, right: 0, left: -25, bottom: 0 }}>
                            <XAxis dataKey="name" fontSize={9} tick={{ fill: '#94A3B8' }} axisLine={false} />
                            <YAxis fontSize={9} tick={{ fill: '#94A3B8' }} axisLine={false} />
                            <Tooltip contentStyle={{ backgroundColor: '#161F33', borderRadius: 8, fontSize: '10px' }} formatter={(val) => `${val} €`} />
                            <Bar dataKey="Einnahmen" fill="#00FF66" radius={[2, 2, 0, 0]} barSize={10}/>
                            <Bar dataKey="Ausgaben" fill="#FF4E4E" radius={[2, 2, 0, 0]} barSize={10}/>
                          </BarChart>
                        </ResponsiveContainer>
                      </Card>
                    </Grid>
                  )}

                  <Grid item xs={12} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Card sx={{ p: 1, flexGrow: 1, overflowY: 'auto' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Letzte Buchungen</Typography>
                      <List disablePadding>
                        {[...buchungen].reverse().slice(0, 4).map((b, i) => {
                          const istBar = pruefeIstBarzahlung(b);
                          return (
                            <ListItem key={i} disableGutters sx={{ py: 0.5, px: 1, borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                              <ListItemText primary={b.titel} secondary={`${istBar ? 'Bar' : 'Karte'} • ${b.datumString?.split('-').reverse().join('.')}`} primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }} secondaryTypographyProps={{ variant: 'caption' }} />
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: b.istEinkommen ? 'success.main' : 'error.main' }}>
                                {b.istEinkommen ? '+' : '-'}{Number(b.betrag).toFixed(2)} €
                              </Typography>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {aktuelleSeite === 1 && (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Card sx={{ width: '100%', maxWidth: '400px' }}>
                    <CardContent component="form" onSubmit={handleAddBuchung}>
                      <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>Buchung eintragen</Typography>
                      <TextField fullWidth size="small" label="Titel" value={bTitel} onChange={(e) => setBTitel(e.target.value)} sx={{ mb: 2 }} required />
                      <TextField fullWidth size="small" type="number" label="Betrag (€)" value={bBetrag} onChange={(e) => setBBetrag(e.target.value)} sx={{ mb: 2 }} inputProps={{ step: "0.01" }} required />
                      <RadioGroup row value={bIstEinnahme} onChange={(e) => setBIstEinnahme(e.target.value)} sx={{ mb: 1, justifyContent: 'center' }}>
                        <FormControlLabel value="ausgabe" control={<Radio color="error" size="small"/>} label="Ausgabe" />
                        <FormControlLabel value="einnahme" control={<Radio color="success" size="small"/>} label="Einnahme" />
                      </RadioGroup>
                      
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <InputLabel>Kategorie</InputLabel>
                        <Select value={bKategorie} label="Kategorie" onChange={(e) => setBKategorie(e.target.value)}>
                          {kategorien.map((kat, idx) => <MenuItem key={idx} value={kat}>{kat}</MenuItem>)}
                        </Select>
                      </FormControl>

                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField fullWidth size="small" placeholder="Eigene Kategorie..." value={neueKategorie} onChange={(e) => setNeueKategorie(e.target.value)} />
                        <Button variant="outlined" color="primary" onClick={handleAddKategorie} sx={{ minWidth: '45px' }}>＋</Button>
                      </Box>

                      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Zahlungsmethode</InputLabel>
                        <Select value={bZahlungsmethode} label="Zahlungsmethode" onChange={(e) => setBZahlungsmethode(e.target.value)}>
                          <MenuItem value="Kartengeld">Kartengeld</MenuItem>
                          <MenuItem value="Bargeld">Bargeld</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField fullWidth size="small" type="date" value={bDatum} onChange={(e) => setBDatum(e.target.value)} sx={{ mb: 3 }} />
                      <Button fullWidth type="submit" variant="contained" color="primary" sx={{ color: '#000' }}>Speichern</Button>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {aktuelleSeite === 2 && (
                <Grid container spacing={2} sx={{ height: '100%' }}>
                  <Grid item xs={12} md={5}>
                    <Card>
                      <CardContent component="form" onSubmit={handleAddSchicht}>
                        <Typography variant="h6" sx={{ mb: 2, color: 'secondary.main' }}>Schicht erfassen</Typography>
                        <TextField fullWidth size="small" type="number" label="Stunden" value={sStunden} onChange={(e) => setSStunden(e.target.value)} sx={{ mb: 2 }} inputProps={{ step: "0.1" }} required />
                        <TextField fullWidth size="small" type="number" label="Stundensatz (€)" value={sStundensatz} onChange={(e) => setSStundensatz(e.target.value)} sx={{ mb: 2 }} required />
                        <TextField fullWidth size="small" type="date" value={sDatum} onChange={(e) => setSDatum(e.target.value)} sx={{ mb: 2 }} />
                        <Button fullWidth type="submit" variant="outlined" color="secondary" sx={{ mb: 1 }}>Eintragen</Button>
                        <Button fullWidth variant="contained" color="secondary" onClick={handleMonatAbschliessen} disabled={aktiveSchichten.length === 0} sx={{ color: '#000' }}>
                          Monat beenden & archivieren
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={7} sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', pb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Offene Schichten ({letzteSchichtenCount})</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                      {aktiveSchichten.map((s, index) => (
                        <Card key={index} sx={{ p: 1.5, minWidth: '140px', flex: '1 1 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(145deg, rgba(0, 240, 255, 0.1) 0%, rgba(0, 0, 0, 0.2) 100%)' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{s.dauerInStunden}h à {s.stundenlohn}€</Typography>
                            <Typography variant="body2" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>{(s.dauerInStunden * s.stundenlohn).toFixed(2)} €</Typography>
                          </Box>
                          <IconButton size="small" color="error" onClick={(e) => handleDeleteSchicht(s, e)}><DeleteIcon fontSize="small" /></IconButton>
                        </Card>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              )}

              {aktuelleSeite === 3 && (
                <Grid container spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>
                  
                  <Grid item xs={12} md={7} sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', pb: 2, pr: 1 }}>
                    
                    {/* FILTER & ANSICHTS-OPTIONEN CARD */}
                    <Card sx={{ p: 1.5, flexShrink: 0, background: 'linear-gradient(145deg, #161F33 0%, #0d1524 100%)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 'bold' }}>Filter & Ansicht</Typography>
                        <IconButton size="small" onClick={() => setZeigeViewOptions(!zeigeViewOptions)} color="primary">
                          <TuneIcon />
                        </IconButton>
                      </Box>

                      {/* Dropdowns für Geldfokus & Monat */}
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <FormControl size="small" fullWidth>
                          <Select value={statistikenFilter} onChange={(e) => setStatistikenFilter(e.target.value)} sx={{ fontSize: '0.8rem' }}>
                            <MenuItem value="Alle">Alle Gelder</MenuItem>
                            <MenuItem value="Bargeld">Bar</MenuItem>
                            <MenuItem value="Kartengeld">Kartengeld</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth>
                          <Select value={zeitraumFilter} onChange={(e) => setZeitraumFilter(e.target.value)} sx={{ fontSize: '0.8rem' }}>
                            <MenuItem value="Alle Zeiten">Alle Zeiten</MenuItem>
                            {verfuegbareMonate.map(monatKey => (
                              <MenuItem key={monatKey} value={monatKey}>{formatMonat(monatKey)}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Toggles zum Ausblenden von Charts */}
                      {zeigeViewOptions && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 2, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                          <FormControlLabel control={<Switch size="small" checked={zeigeBarChart} onChange={(e) => setZeigeBarChart(e.target.checked)} color="primary" />} label={<Typography variant="caption">Monatsbilanz</Typography>} />
                          <FormControlLabel control={<Switch size="small" checked={zeigePieChart} onChange={(e) => setZeigePieChart(e.target.checked)} color="primary" />} label={<Typography variant="caption">Kategorien</Typography>} />
                        </Box>
                      )}
                    </Card>

                    <Grid container spacing={1} sx={{ flexShrink: 0 }}>
                      <Grid item xs={4}>
                        <Card sx={{ p: 1, textAlign: 'center', background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1) 0%, rgba(0,0,0,0.4) 100%)', height: '100%' }}>
                          <LocalAtmIcon color="primary" sx={{ fontSize: 20 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '9px' }}>Tagesbudget</Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main' }}>{tagesBudgetRest.toFixed(2)} €</Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={4}>
                        <Card sx={{ p: 1, textAlign: 'center', background: 'linear-gradient(135deg, rgba(255, 255, 0, 0.1) 0%, rgba(0,0,0,0.4) 100%)', height: '100%' }}>
                          <TrendingUpIcon color="secondary" sx={{ fontSize: 20 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '9px' }}>Sparquote</Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>{sparQuote}%</Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={4}>
                        <Card sx={{ p: 1, textAlign: 'center', background: 'linear-gradient(135deg, rgba(255, 78, 78, 0.1) 0%, rgba(0,0,0,0.4) 100%)', height: '100%' }}>
                          <WarningAmberIcon color="error" sx={{ fontSize: 20 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '9px' }}>Ausgabenfalle</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'error.main', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{topAusgabenfalle}</Typography>
                        </Card>
                      </Grid>
                    </Grid>

                    {zeigeBarChart && monatsDaten.length > 0 && (
                      <Card sx={{ p: 1.5, flexShrink: 0, height: '180px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={monatsDaten} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                            <XAxis dataKey="name" fontSize={10} tick={{ fill: '#94A3B8' }} />
                            <YAxis fontSize={10} tick={{ fill: '#94A3B8' }} axisLine={false} />
                            <Tooltip contentStyle={{ backgroundColor: '#161F33', borderRadius: 8 }} formatter={(val) => `${Number(val).toFixed(2)} €`} />
                            <Bar dataKey="Einnahmen" fill="#00FF66" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="Ausgaben" fill="#FF4E4E" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Card>
                    )}

                    {zeigePieChart && ausgabenNachKategorie.length > 0 && (
                      <Card sx={{ p: 1.5, flexShrink: 0, height: '220px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={ausgabenNachKategorie} cx="50%" cy="45%" innerRadius={45} outerRadius={65} paddingAngle={3} dataKey="value" stroke="none">
                              {ausgabenNachKategorie.map((entry, index) => <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
                            </Pie>
                            <Tooltip formatter={(value) => `${value.toFixed(2)} €`} contentStyle={{ backgroundColor: '#161F33', borderRadius: 8 }} />
                            <Legend verticalAlign="bottom" height={30} iconType="circle" wrapperStyle={{ fontSize: '10px' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Card>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} md={5} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Card sx={{ flex: '1 1 auto', overflowY: 'auto', p: 0 }}>
                      <Box sx={{ p: 2, pb: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Verlauf</Typography>
                        {/* Zeigt im Verlauf-Header an, ob gefiltert wird */}
                        {zeitraumFilter !== 'Alle Zeiten' && <Typography variant="caption" color="secondary.main">{formatMonat(zeitraumFilter)}</Typography>}
                      </Box>
                      <List disablePadding>
                        {gefilterteBuchungen.length === 0 ? (
                          <ListItem><ListItemText primary="Keine passenden Buchungen." sx={{ textAlign: 'center', color: 'text.secondary' }}/></ListItem>
                        ) : (
                          [...gefilterteBuchungen].reverse().map((b, index) => {
                            const istBar = pruefeIstBarzahlung(b);
                            return (
                              <React.Fragment key={index}>
                                <ListItem button onClick={() => openDetailDialog(b)} sx={{ px: 2, py: 1.5 }} secondaryAction={
                                  <IconButton edge="end" color="error" onClick={(e) => handleDeleteBuchung(b, e)}><DeleteIcon fontSize="small" /></IconButton>
                                }>
                                  <ListItemText primary={b.titel} secondary={`${b.kategorie} • ${istBar ? 'Bar' : 'Karte'} • ${b.datumString?.split('-').reverse().join('.')}`} primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
                                  <Typography sx={{ fontWeight: 'bold', mr: 2, fontSize: '0.95rem', color: b.istEinkommen ? 'success.main' : 'error.main' }}>
                                    {b.istEinkommen ? '+' : '-'}{Number(b.betrag).toFixed(2)} €
                                  </Typography>
                                </ListItem>
                                {index < gefilterteBuchungen.length - 1 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />}
                              </React.Fragment>
                            );
                          })
                        )}
                      </List>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Container>

            <Paper sx={{ width: '100%', zIndex: 1000, borderTop: '1px solid rgba(255,255,255,0.1)' }} elevation={3}>
              <BottomNavigation showLabels value={aktuelleSeite} onChange={(e, newValue) => setAktuelleSeite(newValue)} sx={{ bgcolor: 'background.paper', height: '65px' }}>
                <BottomNavigationAction label="Übersicht" icon={<DashboardIcon />} sx={{ '&.Mui-selected': { color: 'primary.main' } }} />
                <BottomNavigationAction label="Eintragen" icon={<AddCardIcon />} sx={{ '&.Mui-selected': { color: 'primary.main' } }} />
                <BottomNavigationAction label="Schichten" icon={<WorkIcon />} sx={{ '&.Mui-selected': { color: 'secondary.main' } }} />
                <BottomNavigationAction label="Statistiken" icon={<BarChartIcon />} sx={{ '&.Mui-selected': { color: 'primary.main' } }} />
              </BottomNavigation>
            </Paper>
          </>
        )}

        <Dialog open={selectedBuchung !== null} onClose={() => setSelectedBuchung(null)} fullWidth maxWidth="xs" PaperProps={{ sx: { bgcolor: 'background.paper', border: '1px solid rgba(0,240,255,0.2)' } }}>
          {selectedBuchung && (
            <>
              <DialogTitle sx={{ fontWeight: 'bold', color: 'primary.main' }}>Eintrag bearbeiten</DialogTitle>
              <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                  <Typography variant="h6">{selectedBuchung.titel}</Typography>
                  <TextField fullWidth size="small" type="date" label="Datum anpassen" value={editDatum} onChange={(e) => setEditDatum(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Box>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setSelectedBuchung(null)} color="inherit">Abbrechen</Button>
                <Button onClick={handleSaveDetail} variant="contained" color="primary" sx={{ color: '#000' }}>Speichern</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}